import { Checkbox } from '@kursk/components/ui/Checkbox/Checkbox';
import { ChangeEvent, useState } from 'react';
import _ from 'lodash';
import styles from './CheckboxSelectMobileFilter.module.scss';
import { BackArrowIcon, CloseIconSmall, SearchIcon, SearchIconBel } from '@kursk/components/ui/icons';
import { Input } from "@kursk/components/ui/Input/Input";
import { themeValue } from "@kursk/themes";

interface IProps<T> {
  items: T[];
  selectedItems?: number[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: (values: any[]) => void;
  getBack: () => void;
  close: () => void;
  placeholder: string;
  disabledArray: { _id: number, name: string }[];
}

interface CommonType {
  _id: number | string;
  name: string;
}

export function CheckboxSelectMobileFilter<T extends CommonType>({
  items,
  selectedItems,
  onChange,
  getBack,
  close,
  placeholder,
  disabledArray
}: IProps<T>) {
  const [defaultSelected, defaultOptions] = selectedItems.length ?
    _.partition(items, ({ _id }) => _.includes(selectedItems, _id)) :
    [[], items];

  const [selected, setSelected] = useState<T[]>(defaultSelected);
  const [options, setOptions] = useState<T[]>(defaultOptions);

  const totalOptions = [...options.filter((opt) => !disabledArray.find((i) => i.name === opt.name)),
    ...disabledArray].filter((el) => selected.find((item) => item._id === el._id)?._id !== el._id);

  const onSubmit = async () => {
    const values = _.map(selected, '_id');
    await onChange(values);
  }

  const onReset = async () => {
    setOptions(_.sortBy([...options, ...selected], 'name'));
    setSelected([]);
    await onChange([]);
  }

  const onSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event?.currentTarget;
    const regexp = new RegExp(value.toLowerCase());
    const excludeIds = _.map(selected, '_id');

    const result = _(items)
      .filter(({ _id }) => !excludeIds.includes(_id))
      .filter(({ name }) => regexp.test(name.toLowerCase()))
      .value();

    setOptions(result);
  }

  const onSelect = (item: any, event: ChangeEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setSelected(_.sortBy([...selected, item], 'name'));
    setOptions(_.sortBy(_.without(options, item), 'name'));
  }

  const onUnselect = (item: any, event: ChangeEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setSelected(_.sortBy(_.without(selected, item), 'name'));
    setOptions(_.sortBy([...options, item], 'name'));
  }

  return (
    <div className={styles.content}>
      <div className={styles.searchContainer}>
        <Input className={styles.search} placeholder={placeholder} onChange={onSearchQueryChange}/>
        {themeValue({
          bel: <SearchIconBel width={16} height={16}/>,
          default: <SearchIcon width={16} height={16}/>
        })}
      </div>
      <button className={styles.backButton} onClick={getBack}><BackArrowIcon width={18} height={13}/></button>
      <button className={styles.closeButton} onClick={close}><CloseIconSmall width={14} height={14}/></button>
      <div className={styles.list}>
        {!!selected.length && (
          <div className={styles.selectedList}>
            {_.map(selected.sort(), (value) => (
              <div className={styles.listItem} key={value._id}>
                <Checkbox value={value._id} onChange={_.partial(onUnselect, value)} checked>
                  {value.name}
                </Checkbox>
              </div>
            ))}
          </div>
        )}
        <div className={styles.optionsList}>
          {_.map(totalOptions, (value) => (
            <div className={styles.listItem} key={value._id}>
              <Checkbox value={value._id} onChange={_.partial(onSelect, value)} checked={false}>
                {value.name}
              </Checkbox>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <button
          className={styles.clearButton}
          onClick={() => {
            onReset();
            getBack();
          }}
        >
          очистить
        </button>
        <button
          className={styles.submitButton}
          onClick={() => {
            onSubmit();
            getBack();
          }}
        >
          применить
        </button>
      </div>
    </div>
  );
}
